import { injectable, inject } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IHttpService, IHttpRequest } from '@/app-modules/http';
import { IGameWinnersService } from '@/app-modules/game-winners/game-winners-service-interface';
import { IGameWinnersViewModel } from '@/app-modules/config/models/game-winners-model';
import { ConfigurationPaths, IChatModsConfiguration, IQuizQuidsWinnersModel } from '@/app-modules/config/models';
import { base64 } from '@/app-modules/utils';
import { IArcadeGameWinnerViewModel } from '../config/models/arcade-game-winners-view-model';
import { IConfigurationService } from '@/app-modules/config';
import { dateFormatter } from '@/app-modules/utils';
import { ITogglesService } from '@/app-modules/config';

@injectable()
class GameWinnersService implements IGameWinnersService {
  constructor(
    @inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: IHttpService,
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.ITogglesService) private readonly toggleService: ITogglesService,
  ) { }

  public dateFormat: string = 'DD/MM/Y[,] HH:mm:ss';
  public async getGameWinners(gameName: string): Promise<IGameWinnersViewModel[]> {
    const configuration = await this.configurationService.getConfiguration();
    const urlFriendlyGameName = await this.getUrlFriendlyGameNameFromNonUrlFriendlyGameName(configuration, gameName);
    const countryDetails = configuration.getConfigurationByPath(ConfigurationPaths.regiontime.details);

    const httpRequest: IHttpRequest = {
      Url: `/game-winners/all/${urlFriendlyGameName}`
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest, true);

    if (result.status !== 200 || result.data == null) {
      throw new Error('Could not retrieve game configuration.');
    }
    const winners = (result.data.winners as IGameWinnersViewModel[]) || [];
    winners.forEach((winner) => this.setFormattedStamps(winner.gameWinners, countryDetails));
    winners.forEach((winner) => this.setFormattedStamps(winner.jackpotWinners, countryDetails));
    winners.forEach((winner) => this.setFormattedStamps(winner.hamsterRaceGameWinners, countryDetails));
    winners.forEach((winner) => this.setFormattedStamps(winner.hamsterRaceLargestWinners, countryDetails));
    return winners;
  }

  public async getQuizQuidsWinners(userId: number, crossCheckId: number): Promise<IQuizQuidsWinnersModel[]> {
    const encodedAuthorization = base64.Encode(`${userId}:${crossCheckId}`);
    const countryDetails = await this.getCountryDetails();

    const httpRequest: IHttpRequest = {
      Url: `/reports/quiz-quids/winners`,
      XChatModsAuthorization: `${encodedAuthorization}`
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest, true);
    if (result.status !== 200 || result.data == null){
      throw new Error('Could not retrieve game configuration.');
    }

    const winners: IQuizQuidsWinnersModel[] = result.data;
    return this.setQuizQuidsFormattedStamps(winners);
  }

  public async getArcadeWinners(): Promise<IArcadeGameWinnerViewModel[]> {
    const httpRequest: IHttpRequest = {
      Url: '/game-winners/arcade'
    } as IHttpRequest;
    const result = await this.httpService.get(httpRequest, true);

    const countryDetails = await this.getCountryDetails();
    if (result.status !== 200 || result.data == null) {
      throw new Error('Could not retrieve arcade winners.');
    }
    return this.setFormattedStamps(result.data.winners as IArcadeGameWinnerViewModel[], countryDetails);
  }


  private async getUrlFriendlyGameNameFromNonUrlFriendlyGameName(configuration: IChatModsConfiguration, nonUrlFriendlyGameName: string): Promise<string> {
    if (configuration.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_gameengine_config_for_winners_service_url_islive) === 'true') {
      const gamesConfiguration = await this.configurationService.getGameConfiguration();
      const singleGameConfiguration = gamesConfiguration.Games.find((game) => game.Name === nonUrlFriendlyGameName);

      if (singleGameConfiguration && singleGameConfiguration.UrlFriendlyGameName !== '') {
        return singleGameConfiguration.UrlFriendlyGameName;
      }

      // fallback to old logic if we found no UrlFriendlyGameName
    }

    // deprecated: we can remove these when use_gameengine_config_for_winners_service_url_islive is on in the UK and the UrlFriendlyGameName is set in game engine management
    const gameNamesThatRequireDashes: any[] = ['Hamster Race', 'Bingo Roulette', 'Tombola Roulette', 'Roller Coaster', 'Bingo Lite', 'Picture Bingo', 'Bubble Bingo'];
    if (gameNamesThatRequireDashes.includes(nonUrlFriendlyGameName)) {
      return nonUrlFriendlyGameName.replace(/(^\s+|[^a-zA-Z0-9 ]+|\s+$)/g, '').replace(/\s+/g, '-').replace(/ +/g, '').toLowerCase();
    }
    else { // we should retain the following as International will need this (nothing to stop them implementing UrlFriendlyGameName and the above ft)
      return nonUrlFriendlyGameName.toLowerCase().replace(/ +/g, '');
    }
  }

  private async getCountryDetails(): Promise<string> {
    const configuration = await this.configurationService.getConfiguration();
    const countryDetails = configuration.getConfigurationByPath(ConfigurationPaths.regiontime.details);
    return countryDetails;
  }

  private setQuizQuidsFormattedStamps<T extends { stamp: string, formattedStamp?: string }>(winners: T[]): T[] {
    winners.forEach((winner) => winner.formattedStamp = dateFormatter.toFormatted(winner.stamp, this.dateFormat));
    return winners;
  }

  private setFormattedStamps<T extends { stamp: string, formattedStamp?: string }>(winners: T[], countryDetails: string): T[] {
    if (winners) {

      if (countryDetails) {
        const split = countryDetails.split(',');
        if (split.length === 2) {
          const region = split[1];
          if (region) {
            winners.forEach((winner) => winner.formattedStamp = this.formatStamp(winner.stamp, region));
          }
        }
      }
      else {
        winners.forEach((winner) => winner.formattedStamp = this.formatStampToLocalTimezone(winner.stamp));
      }
    }
    return winners;
  }

  private formatStamp(stamp: string, timezone: string) {
    return dateFormatter.toTimezoneFormatted(stamp, timezone, this.dateFormat);
  }

  private formatStampToLocalTimezone(stamp: string) {
    const convertToDate = new Date(stamp);
    return convertToDate.toLocaleString();
  }
}
export { IGameWinnersService, GameWinnersService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/game-winners/game-winners-service.ts